import LocalStorage, {LocalStorageType} from 'services/LocalStorage';
import {StudentData} from './AdminApi/AdminApiModels';
import {RefreshTokenResponse} from './AuthApi/AuthApiModels';

export enum URLType {
  api = 'https://',
}

export enum Domain {
  authStage = 'uclass-authorization-gateway-5k0cbisu.ew.gateway.dev',
  authDev = 'localhost-5k0cbisu.ew.gateway.dev',
  users = 'uclass-users-gateway-5k0cbisu.ew.gateway.dev',
  class = 'uclass-class-gateway-5k0cbisu.ew.gateway.dev',
  groups = 'uclass-groups-gateway-5k0cbisu.ew.gateway.dev',
  conference = 'start-class-test-qjctldh3cq-ew.a.run.app',
}

export enum RequestType {
  signInWithSocialNetworks,
  refreshToken,
  signOut,
  currentUser,
  classesMetadata,
  createClass,
  classes,
  deleteUser,
  changePhoto,
  deletePhoto,
  startConference,
  analyticsTranscription,
  analyticsReaction,
  analyticsAttention,
  getGroups,
  getGroupInfo,
  updateGroup,
  deleteGroup,
  createGroup,
  getTenant,
  updateTenant,
  getTeachers,
  updateTeachers,
  sendReaction,
  deleteSchedule,
  getRecording,
  getAttendees,
}

export interface UploadFileType {
  name: string;
  filename: string;
  filepath: string;
  filetype: string;
}

export const getRequestString = (requestType: RequestType, query?: string) => {
  switch (requestType) {
    case RequestType.signInWithSocialNetworks:
      return '/login';
    case RequestType.refreshToken:
      return '/reissue';
    case RequestType.signOut:
      return '/revoke';
    case RequestType.currentUser:
      return '/users/current';
    case RequestType.deleteUser:
      return `/users/delete/${query}`;
    case RequestType.changePhoto:
      return '/users/photo';
    case RequestType.deletePhoto:
      return '/users/photo';
    case RequestType.getTeachers:
    case RequestType.updateTeachers:
      return '/users/teachers';
    case RequestType.classesMetadata:
      return '/classes/metadata';
    case RequestType.createClass:
      return '/classes/add';
    case RequestType.classes:
      return '/classes/get';
    case RequestType.startConference:
      return '/classes/start';
    case RequestType.analyticsTranscription:
      return `/classes/analytics/transcription/${query}`;
    case RequestType.analyticsReaction:
      return `/classes/analytics/reaction/${query}`;
    case RequestType.analyticsAttention:
      return `/classes/analytics/attention/${query}`;
    case RequestType.getAttendees:
      return `/classes/analytics/attendees/${query}`;
    case RequestType.getRecording:
      return `/classes/recording/${query}`;
    case RequestType.deleteSchedule:
      return '/classes/delete';
    case RequestType.getGroups:
    case RequestType.updateGroup:
    case RequestType.createGroup:
      return '/groups';
    case RequestType.getGroupInfo:
    case RequestType.deleteGroup:
      return `/groups/${query}`;
    case RequestType.getTenant:
    case RequestType.updateTenant:
      return '/tenant';
    case RequestType.sendReaction:
      return '/classes/analytics/reaction';
    default:
      return '';
  }
};

export const getContentType = (requestType: RequestType) => {
  switch (requestType) {
    default:
      return 'application/json';
  }
};

export const getAcceptType = (requestType: RequestType) => {
  switch (requestType) {
    case RequestType.getGroups:
      return '*/*';
    default:
      return 'application/json';
  }
};

export const getAuthentication = async (requestType: RequestType) => {
  switch (requestType) {
    case RequestType.currentUser:
    case RequestType.classesMetadata:
    case RequestType.createClass:
    case RequestType.classes:
    case RequestType.deleteUser:
    case RequestType.changePhoto:
    case RequestType.deletePhoto:
    case RequestType.startConference:
    case RequestType.analyticsTranscription:
    case RequestType.analyticsReaction:
    case RequestType.analyticsAttention:
    case RequestType.getGroups:
    case RequestType.updateGroup:
    case RequestType.createGroup:
    case RequestType.deleteSchedule:
    case RequestType.getGroupInfo:
    case RequestType.deleteGroup:
    case RequestType.updateTenant:
    case RequestType.getTenant:
    case RequestType.updateTeachers:
    case RequestType.getTeachers:
    case RequestType.sendReaction:
    case RequestType.getRecording:
    case RequestType.getAttendees:
      return (
        'Bearer ' + (await LocalStorage.getValue(LocalStorageType.accessToken))
      );
    default:
      return undefined;
  }
};

export const getRequestType = (requestType: RequestType) => {
  switch (requestType) {
    case RequestType.signInWithSocialNetworks:
    case RequestType.refreshToken:
    case RequestType.signOut:
    case RequestType.createClass:
    case RequestType.startConference:
    case RequestType.createGroup:
    case RequestType.updateTeachers:
    case RequestType.sendReaction:
      return 'POST';
    case RequestType.deleteUser:
    case RequestType.deleteSchedule:
      return 'DELETE';
    case RequestType.changePhoto:
      return 'PATCH';
    case RequestType.deletePhoto:
      return 'DELETE';
    case RequestType.updateGroup:
    case RequestType.updateTenant:
    case RequestType.getRecording:
      return 'PUT';
    default:
      return 'GET';
  }
};

export const getDomain = (requestType: RequestType) => {
  switch (requestType) {
    case RequestType.currentUser:
    case RequestType.deleteUser:
    case RequestType.changePhoto:
    case RequestType.deletePhoto:
    case RequestType.getTeachers:
    case RequestType.updateTeachers:
      return Domain.users;
    case RequestType.classesMetadata:
    case RequestType.createClass:
    case RequestType.classes:
    case RequestType.sendReaction:
    case RequestType.analyticsTranscription:
    case RequestType.analyticsReaction:
    case RequestType.analyticsAttention:
    case RequestType.deleteSchedule:
    case RequestType.getRecording:
    case RequestType.getAttendees:
      return Domain.class;
    case RequestType.getGroups:
    case RequestType.updateGroup:
    case RequestType.createGroup:
    case RequestType.getGroupInfo:
    case RequestType.getTenant:
    case RequestType.updateTenant:
      return Domain.groups;
    case RequestType.startConference:
      return Domain.class;
    default:
      return process.env.NODE_ENV == 'development'
        ? Domain.authDev
        : Domain.authStage;
  }
};

const createBody = (
  requestType: RequestType,
  body?: Object | Array<StudentData> | undefined,
) => {
  switch (requestType) {
    default:
      return JSON.stringify(body);
  }
};

interface Request {
  requestType: RequestType;
  params: {
    body?: Object;
    urlParams?: Object;
    params?: string[];
  };
  serverType: URLType;
  domain: Domain;

  resolve: any | PromiseLike<any>;
  reject: any | PromiseLike<any>;
}

class ApiManager {
  private queue: Request[] = [];
  private isUnauthorized = false;

  request = async <T>(
    requestType: RequestType,
    params: {
      body?: Object | Array<StudentData>;
      urlParams?: Object;
      params?: string[];
      query?: string;
    },
    serverType: URLType = URLType.api,
    domain: Domain = getDomain(requestType),
  ) => {
    let headers: HeadersInit = {
      Accept: getAcceptType(requestType),
      'Content-Type': getContentType(requestType),
    };

    const auth = await getAuthentication(requestType);
    if (auth) {
      headers.Authorization = auth;
    }

    let paramsString = '';
    if (params.urlParams) {
      for (const [key, value] of Object.entries(params.urlParams)) {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(item => {
              paramsString += paramsString.length === 0 ? '?' : '&';
              paramsString += key + '=' + item;
            });
          } else {
            paramsString += paramsString.length === 0 ? '?' : '&';
            paramsString += key + '=' + value;
          }
        }
      }
    }

    const url =
      serverType +
      domain +
      getRequestString(requestType, params.query) +
      paramsString;

    console.log(url, headers);

    return new Promise<T>((resolve, reject) => {
      if (this.isUnauthorized && requestType != RequestType.refreshToken) {
        this.queue.push({
          requestType,
          params,
          serverType,
          domain,
          resolve,
          reject,
        });
        return;
      }

      fetch(url, {
        method: getRequestType(requestType),
        headers,
        body: createBody(requestType, params.body),
      })
        .then(async response => {
          console.log(response.status);

          if (response.status === 401) {
            if (this.isUnauthorized) {
              this.isUnauthorized = false;
              LocalStorage.delete(LocalStorageType.refreshToken);
              reject();
              return;
            }

            this.isUnauthorized = true;
            this.queue.push({
              requestType,
              params,
              serverType,
              domain,
              resolve,
              reject,
            });

            await this.sendRefreshRequest();
            return;
          }

          if (!response.ok && response.status >= 400 && response.status < 600) {
            this.isUnauthorized = false;
            reject(response.status);
            return;
          }

          const data = await response.json();
          resolve(data);
        })
        .catch(err => {
          console.log('err', err);
          reject(err);
        });
    });
  };

  private sendRefreshRequest = async () => {
    const refreshToken = await LocalStorage.getValue(
      LocalStorageType.refreshToken,
    );

    const data = await this.request<RefreshTokenResponse>(
      RequestType.refreshToken,
      {
        body: {
          refresh_token: refreshToken,
        },
      },
    );

    await LocalStorage.save(data.access_token, LocalStorageType.accessToken);
    this.isUnauthorized = false;

    for (const request of this.queue) {
      try {
        const result = await this.request(
          request.requestType,
          request.params,
          request.serverType,
          request.domain,
        );

        request.resolve(result);
      } catch (err) {
        request.reject(err);
      }
    }
  };
}

const apiManager = new ApiManager();

export default apiManager;
